import React, { FunctionComponent, useState, MouseEvent } from 'react';
import { Box, Paper, makeStyles, Typography, Link, CircularProgress } from '@material-ui/core';
import Modal from '../../../../components/Modal';
import DeleteDialog from '../../../../components/DeleteDialog';
import FileUploadDialog from '../../../../components/FileUploadDialog';
import { File } from '../../../../store/modules/application/types';
import { doubleEncodeURL } from '../../../../utils/utils';
import Button from '@material-ui/core/Button';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import InfiniteScrollTableView from '../../../../components/InfiniteScrollTableView';
import RefreshIcon from '@material-ui/icons/Refresh';
import { Api } from '../../../../store/services/services';
import { useAuthenticationContext } from '../../../../authentication';

type FilesProps = {
    data: File[];
    applicationId: string;
    loading: boolean;
    getAuthenticationToken: Function;
    limit: number;
    toggleFileRequestTrigger: Function;
    hasMore: boolean;
    errorStatus: boolean;
    deleteLicenseFile: Function;
    removeDeletedFile: Function;
};

interface IColumn {
    id: 'fileName|string' | 'uploadedDate|date' | 'size|size' | 'metadata.documenttype|string';
    label: string;
    maxWidth?: number;
    align?: 'right' | 'left' | 'center';
    format?: (value: number) => string;
    field: string;
    noSort?: boolean;
}

const columns: IColumn[] = [
    { id: 'fileName|string', label: 'Name', maxWidth: 300, field: 'fileName', noSort: true },
    { id: 'uploadedDate|date', label: 'Date Uploaded', maxWidth: 200, field: 'uploadedDate', noSort: true },
    {
        id: 'size|size',
        label: 'Size',
        align: 'left',
        maxWidth: 200,
        field: 'size',
        noSort: true,
    },
    {
        id: 'metadata.documenttype|string',
        label: 'Type',
        align: 'left',
        maxWidth: 200,
        field: 'documenttype',
        noSort: true,
    },
];

const useStyles = makeStyles({
    searchIconContainer: {
        width: '100%',
        maxHeight: 300,
        color: 'gray',
        paddingTop: '40px',
        paddingBottom: '150px',
    },
    noResultFound: {
        fontSize: '24px',
        color: '#484848',
        lineHeight: '24px',
        fontWeight: 600,
        marginTop: '3rem',
        textAlign: 'center',
    },
    noResultFoundMessage2: {
        fontSize: '18px',
        color: '#484848',
        lineHeight: '24px',
        marginTop: '20px',
        fontWeight: 500,
        textAlign: 'center',
    },
    noResultFoundUploadFile: {
        fontSize: '18px',
        color: '#0277BD',
        lineHeight: '24px',
        fontWeight: 500,
        textAlign: 'center',
        marginTop: '20px',
        cursor: 'pointer',
    },
    uploadButton: {
        color: '#0277BD',
        marginRight: '40px',
        fontWeight: 600,
        cursor: 'pointer',
        letterSpacing: '0.3px,',
        display: 'flex',
        alignItems: 'center',
        marginBottom: '25px',
        justifyContent: 'flex-end',
    },
    uploadButtonContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
    link: {
        color: '#0277BD',
        textAlign: 'center',
        marginLeft: '8px',
        marginRight: '8px',
        '&:hover': {
            cursor: 'pointer',
        },
    },
    circularProgress: {
        textAlign: 'center',
        marginTop: '85px',
        color: '#0277BD',
    },
});

const Files: FunctionComponent<FilesProps> = (props) => {
    const {
        data,
        applicationId,
        loading,
        getAuthenticationToken,
        toggleFileRequestTrigger,
        hasMore,
        errorStatus,
        deleteLicenseFile,
        removeDeletedFile,
    } = props;

    const classes = useStyles();

    const { getAccessToken, scopes: authorizationScopes } = useAuthenticationContext();

    const [fileId, setFileId] = useState<string>('');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState<boolean>(false);
    const [fileDownloadLoading, setFileDownloadLoading] = useState<boolean>(false);

    const deleteFileDocument = async (applicationId: string, fileId: string) => {
        const encodedFileId = doubleEncodeURL(fileId);

        const token = await getAccessToken({ scopes: [authorizationScopes['licenses.read']] });

        try {
            await Api.deleteFileDocument(token.accessToken, applicationId, encodedFileId);

            removeDeletedFile(fileId);

            deleteLicenseFile();
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);

            deleteLicenseFile(err);
        }
    };

    const getFileToken = async (applicationId: string, fileId: string) => {
        const authToken = await getAccessToken({ scopes: [authorizationScopes['licenses.read']] });

        return (await Api.getFileToken(authToken.accessToken, applicationId, fileId)).token;
    };

    const downloadFileDocument = async (applicationId: string, fileId: string, fileName: string) => {
        setFileDownloadLoading(true);

        const encodedFileId = doubleEncodeURL(fileId);

        try {
            const fileToken = await getFileToken(applicationId, encodedFileId);

            Api.downloadFileDocument(encodedFileId, fileToken, fileName);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error);
        } finally {
            setFileDownloadLoading(false);
        }
    };

    const handleUploadFile = (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation();

        setIsUploadModalOpen(true);
    };

    /* Handle delete case button */
    const handleDeleteCase = (id: string, e: any) => {
        setFileId(id);

        setIsDeleteModalOpen(true);

        e.stopPropagation();
    };

    const onClose = () => {
        setIsDeleteModalOpen(false);

        setIsUploadModalOpen(false);
    };

    const onDelete = async () => {
        setIsDeleteModalOpen(false);

        await deleteFileDocument(applicationId, fileId);
    };

    const onClickRow = (id: number, e: any) => {
        e.stopPropagation();
    };

    const loadMore = () => {
        if (hasMore) {
            toggleFileRequestTrigger();
        }
    };

    const handleDownloadCase = (file: File) => {
        downloadFileDocument(applicationId, file.id, file.fileName);
    };

    const isLoading = loading || fileDownloadLoading;

    if (errorStatus) {
        return (
            <div className="verticalFillContainer">
                <div className={classes.uploadButtonContainer}>
                    <Button
                        startIcon={<RefreshIcon />}
                        className={classes.uploadButton}
                        onClick={() => toggleFileRequestTrigger(true)}
                    >
                        RELOAD FILES
                    </Button>
                </div>
                <Paper className="case-entries-container verticalFillContent">
                    {isLoading ? (
                        <Box className={classes.circularProgress} display="flex" justifyContent="center">
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Box className={classes.searchIconContainer} display="flex" flexDirection="column">
                            <Box className={classes.noResultFound} display="flex" justifyContent="center">
                                Unable to load files. Please try{' '}
                                <Link className={classes.link} onClick={() => toggleFileRequestTrigger(true)}>
                                    reloading
                                </Link>{' '}
                                in a few moments.
                            </Box>
                        </Box>
                    )}
                </Paper>
            </div>
        );
    }

    return (
        <div className="verticalFillContainer">
            <div className={classes.uploadButtonContainer}>
                <Button
                    startIcon={<CloudUploadIcon />}
                    className={classes.uploadButton}
                    onClick={(e: MouseEvent<HTMLElement>) => {
                        handleUploadFile && handleUploadFile(e);
                    }}
                    //  Only disable the button if it is the initial load of files.
                    //  If it is an additional loading due to the infinite scrolling
                    //  then don't disable for a smoother experience.
                    disabled={isLoading && !data.length}
                >
                    UPLOAD FILE
                </Button>
            </div>
            <Paper className="case-entries-container verticalFillContent">
                {/* Only display circular progress if it is the initial load of files.
                    If it is an additional loading due to the infinite scrolling then don't
                    display the circular progress for a smoother experience.  */}
                {isLoading && !data.length ? (
                    <Box className={classes.circularProgress} display="flex" justifyContent="center">
                        <CircularProgress />
                    </Box>
                ) : data.length ? (
                    <InfiniteScrollTableView
                        columns={columns}
                        data={data}
                        count={data.length}
                        handleDeleteCase={handleDeleteCase}
                        handleDownloadCase={handleDownloadCase}
                        onClickRow={onClickRow}
                        deleteFlag={true}
                        downloadFlag
                        hasMore={hasMore}
                        loadMore={loadMore}
                    />
                ) : (
                    <Box className={classes.searchIconContainer} display="flex" flexDirection="column">
                        <Box className={classes.noResultFound} display="flex" justifyContent="center">
                            No files yet
                        </Box>
                        <Box className={classes.noResultFoundMessage2} display="flex" justifyContent="center">
                            All your files will be stored here
                        </Box>
                        <Box display="flex" justifyContent="center">
                            <Typography
                                className={classes.noResultFoundUploadFile}
                                onClick={(e: MouseEvent<HTMLElement>) => handleUploadFile(e)}
                            >
                                Upload File
                            </Typography>
                        </Box>
                    </Box>
                )}
                {(isDeleteModalOpen || isUploadModalOpen) && (
                    <Modal open={true} maxWidth={'md'} fullWidth={true} minHeight={'10vh'}>
                        {isDeleteModalOpen && (
                            <DeleteDialog
                                onClose={onClose}
                                onDelete={onDelete}
                                contentTitle="Are you sure you'd like to proceed with this action?"
                                content=""
                            />
                        )}
                        {isUploadModalOpen && (
                            <FileUploadDialog
                                onClose={onClose}
                                licenseId={applicationId}
                                getAuthenticationToken={getAuthenticationToken}
                                getFileList={() => null}
                                toggleFileRequestTrigger={toggleFileRequestTrigger}
                            />
                        )}
                    </Modal>
                )}
            </Paper>
        </div>
    );
};

export default Files;
